import React, { ReactNode, useEffect, useState } from 'react';
import {
  Alert, Button, Checkbox, Input, Modal, Progress, Radio, Skeleton, Slider, Space, Steps, Tag, Tooltip,
  Upload, notification,
} from 'antd';
import { ExportOutlined, InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import DriveSearch from '../pathfinder/DriveSearch';
import useFloorPlanRenders from './useFloorPlanRenders';
import { driveFileUrl } from '../pathfinder/driveLink';
import { ancanaApi } from '../../../api/ancanaApi';
import {
  FLOOR_PLAN_SOURCE_MIMES, FLOOR_PLAN_UPLOAD_MIMES, IDesignLevel, IFloorPlan, IUnitLevel, LevelLayout, levelLabel,
} from '../../../types/models/floorPlans';
import { LAYOUT_OPTIONS } from '../../../types/models/levels';
import { IDriveNode } from '../../../types/models/pathfinder';

const { Step } = Steps;

/**
 * The checkerboard. Without it a transparent background is indistinguishable
 * from a white one, and the whole point of step 3 is to let somebody see the
 * difference before they publish it.
 */
const CHECKERBOARD = `
  background-image:
    linear-gradient(45deg, #e8e8e8 25%, transparent 25%),
    linear-gradient(-45deg, #e8e8e8 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #e8e8e8 75%),
    linear-gradient(-45deg, transparent 75%, #e8e8e8 75%);
  background-size: 16px 16px;
  background-position: 0 0, 0 8px, 8px -8px, -8px 0;
`;

const Container = styled.div`
  .levels {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;

    .levels-label { color: var(--ancana-gray); font-size: 12px; }
  }

  .step-body {
    margin-top: 20px;
    min-height: 380px;
  }

  .preview {
    ${CHECKERBOARD}
    border: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    min-height: 320px;

    img {
      max-width: 100%;
      max-height: 60vh;
      object-fit: contain;
    }
  }

  .side-by-side {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    .pane-title {
      font-weight: 600;
      margin-bottom: 6px;
    }

    iframe, .preview {
      width: 100%;
      height: 60vh;
      border: 1px solid #f0f0f0;
    }
  }

  .filmstrip {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    padding: 8px 0;

    .frame {
      flex: none;
      width: 84px;
      height: 84px;
      border: 2px solid transparent;
      border-radius: 4px;
      cursor: pointer;
      ${CHECKERBOARD}

      img { width: 100%; height: 100%; object-fit: contain; }

      &.active { border-color: var(--ancana-primary, #1890ff); }
    }
  }

  .chosen {
    font-family: monospace;
    font-size: 12px;
    overflow-wrap: anywhere;
    color: var(--ancana-gray);
  }

  .cost {
    color: var(--ancana-gray);
    font-size: 12px;
  }

  .saved {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 20px;

    .facts {
      margin: 10px 0 0;
      padding: 0;
      list-style: none;
      color: var(--ancana-gray);
      font-size: 12px;

      li { margin-bottom: 2px; overflow-wrap: anywhere; }
    }

    .next {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .choice {
      border: 1px solid #f0f0f0;
      border-radius: 4px;
      padding: 10px 12px;
      text-align: left;
      height: auto;
      white-space: normal;

      .choice-title { font-weight: 600; }
      .choice-why { color: var(--ancana-gray); font-size: 12px; }
    }

    .layout {
      border: 1px solid #f0f0f0;
      border-radius: 4px;
      padding: 10px 12px;

      .layout-title { font-weight: 600; margin-bottom: 6px; }
      .layout-why { color: var(--ancana-gray); font-size: 12px; margin-top: 6px; }
    }
  }

  .versions {
    margin-top: 16px;

    .row {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 6px 0;
      border-top: 1px solid #f5f5f5;
      font-size: 12px;
      color: var(--ancana-gray);

      .thumb {
        ${CHECKERBOARD}
        width: 40px;
        height: 40px;
        flex: none;
        border: 1px solid #f0f0f0;
        cursor: pointer;

        img { width: 100%; height: 100%; object-fit: contain; }
      }

      .who { color: rgba(0, 0, 0, 0.85); }
    }
  }
`;

const when = (iso?: string | null) => (iso ? dayjs(iso).format('D MMM YYYY') : '');

export interface FloorPlanWizardProps {
  /** The design this plan belongs to. Required even for a unit override. */
  designId: number;
  /** Set only when making a per-unit override. */
  unitId?: number;
  /**
   * The level (storey) to open on. Defaults to the lowest. Only matters for a
   * multi-level design, where the wizard shows a picker; a single-level design
   * has one answer and never asks.
   */
  levelId?: number;
  /** Shown in the header and used to seed the file search. */
  recordName?: string;
  onPublished?: (plan: IFloorPlan) => void;
  /**
   * Controlled mode. Omit both and the wizard owns its own open state, opened
   * by clicking `children` — the original and still the common case. Pass them
   * to open it from somewhere else, as the template drawer does when a
   * generation needs a floor plan that does not exist yet.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** The trigger. Same convention as RecordGalleriesModal. */
  children?: ReactNode;
}

/**
 * Generate a floor plan, end to end.
 *
 * Self-contained on purpose: everything it needs arrives as props and it owns
 * its own trigger, so it can sit in a table row action today and inside the
 * presentations template drawer tomorrow ("no floor plan — generate one")
 * without being taken apart first.
 *
 * Four steps, ordered by cost. Everything free happens before anything
 * billable, and everything after the billable step is re-runnable without
 * repeating it — a generation is ~$0.07 and a re-cut is free, so the sliders
 * in step 3 must never be a reason to generate again.
 */
const FloorPlanWizard = ({
  designId, unitId, levelId: levelIdProp, recordName, onPublished, open: openProp, onOpenChange,
  children,
}: FloorPlanWizardProps) => {
  const [openState, setOpenState] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openState;
  const setOpen = (next: boolean) => {
    if (!controlled) setOpenState(next);
    onOpenChange?.(next);
  };
  /**
   * Which face the wizard is showing.
   *
   * `saved` is the front door, not a step: most visits to a design that already
   * has a floor plan end there, with somebody looking at it and closing the
   * window. Rendering it as a fifth entry in the `Steps` bar would promise a
   * path that runs onward to publishing, when the common case is that it
   * does not.
   *
   * `upload` is the hand-made route: a finished image published as it is, with
   * none of the four steps behind it, so it is not drawn as one either.
   */
  const [view, setView] = useState<'saved' | 'flow' | 'upload'>('flow');
  const [step, setStep] = useState(0);
  const [source, setSource] = useState<IDriveNode>();
  const [instruction, setInstruction] = useState('');
  const [revision, setRevision] = useState('');
  const [mode, setMode] = useState<'background' | 'all'>('background');
  const [whitePoint, setWhitePoint] = useState(250);
  const [linePoint, setLinePoint] = useState(200);
  const [attested, setAttested] = useState(false);
  const [notes, setNotes] = useState('');
  const [published, setPublished] = useState<IFloorPlan>();
  const [sourceUrl, setSourceUrl] = useState<string>();
  const [sourceError, setSourceError] = useState<string>();
  const [uploadFile, setUploadFile] = useState<File>();
  const [uploadUrl, setUploadUrl] = useState<string>();

  // Which published version the saved view is showing. Defaults to the current
  // one; clicking through the history moves it without changing what is live.
  const [shownPlanId, setShownPlanId] = useState<number>();

  /**
   * The design's storeys, read on open. Every plan, render and version
   * belongs to one, and on a multi-level design the server refuses to guess
   * which: an upstairs plan published as the ground floor would be wrong on
   * every brochure without anybody noticing. `undefined` while loading; if
   * the read fails the wizard carries on unscoped, which is still right for
   * the single-level designs that are almost all of them.
   */
  const [levels, setLevels] = useState<IDesignLevel[]>();
  const [levelId, setLevelId] = useState<number>();
  const [levelsRead, setLevelsRead] = useState(false);
  const level = levels?.find(l => l.id === levelId);
  const multiLevel = (levels?.length ?? 0) > 1;

  const renders = useFloorPlanRenders(designId, unitId, levelId);
  const {
    active, chain, plans, currentPlan, isInherited, isLoading, isWorking, error,
  } = renders;

  const shown = plans.find(p => p.id === shownPlanId) || currentPlan;

  /**
   * The unit's own row for this level, when the wizard is for a unit. `null`
   * once read and absent. Only needed for the layout: on a unit with its own
   * plan, the layout that counts is this row's, not the design level's.
   */
  const [unitLevel, setUnitLevel] = useState<IUnitLevel | null>();
  const [savingLayout, setSavingLayout] = useState(false);

  /**
   * Read what is already saved, every time the window opens.
   *
   * Before this the wizard opened on "pick a source" whether or not the design
   * had a floor plan, so the only offered action was to generate another one —
   * at a cost — and the published version was invisible from the place that
   * published it. It also made the reassurance on step 2 ("you can close this
   * window, the work carries on") false: the work did carry on, but the result
   * was not here on the way back.
   */
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLevelsRead(false);
    ancanaApi.get('design-levels/', { params: { design: designId, page_size: 50 } })
      .then(({ data }) => {
        if (cancelled) return;
        const rows: IDesignLevel[] = Array.isArray(data) ? data : data?.results || [];
        rows.sort((a, b) => a.index - b.index);
        setLevels(rows);
        setLevelId(rows.find(l => l.id === levelIdProp)?.id ?? rows[0]?.id);
      })
      .catch(() => { if (!cancelled) setLevels([]); })
      .finally(() => { if (!cancelled) setLevelsRead(true); });
    return () => { cancelled = true; };
    // Opening is the event; the prop only seeds the first choice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, designId]);

  useEffect(() => {
    if (!open || !levelsRead) return undefined;
    let cancelled = false;
    // The landing decision rides on the promise rather than on `isLoading`,
    // which is not yet true in the commit that starts the fetch — reading it
    // here would land on the picker every time and then never correct itself.
    renders.hydrate().then(plan => {
      if (!cancelled) setView(plan ? 'saved' : 'flow');
    });
    return () => { cancelled = true; };
    // `hydrate` is stable in designId/unitId/levelId, and re-running it on
    // every render would be a request loop. Opening, and picking another
    // level, are the events worth reacting to.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, levelsRead, designId, unitId, levelId]);

  useEffect(() => {
    setUnitLevel(undefined);
    if (!open || !unitId || !levelId) return undefined;
    let cancelled = false;
    ancanaApi.get('unit-levels/', { params: { unit: unitId, level: levelId } })
      .then(({ data }) => {
        const rows: IUnitLevel[] = Array.isArray(data) ? data : data?.results || [];
        if (!cancelled) setUnitLevel(rows.find(r => r.level === levelId) ?? null);
      })
      .catch(() => { if (!cancelled) setUnitLevel(null); });
    return () => { cancelled = true; };
  }, [open, unitId, levelId]);

  /**
   * Whose layout the brochure uses for this level: the same row that supplies
   * the plan (`Unit.resolved_levels`). A unit showing its design's plan uses
   * the design level's layout, so changing it here changes it for every unit
   * that shares the plan — which the control says.
   */
  const layoutOwner: 'unit' | 'design' | undefined = (() => {
    if (unitId && !isInherited) return unitLevel ? 'unit' : undefined;
    return level ? 'design' : undefined;
  })();
  const layout: LevelLayout = (layoutOwner === 'unit' ? unitLevel?.layout_override : level?.layout_override) ?? '';

  const changeLayout = async (next: LevelLayout) => {
    const url = layoutOwner === 'unit' ? `unit-levels/${unitLevel?.id}/` : `design-levels/${level?.id}/`;
    setSavingLayout(true);
    try {
      await ancanaApi.patch(url, { layout_override: next });
      if (layoutOwner === 'unit') {
        setUnitLevel(row => (row ? { ...row, layout_override: next } : row));
      } else {
        setLevels(prev => prev?.map(l => (l.id === level?.id ? { ...l, layout_override: next } : l)));
      }
    } catch (e: any) {
      notification.error({
        message: 'Could not change the layout',
        description: e?.response?.data?.detail || e?.message || '',
        placement: 'bottomRight',
      });
    } finally {
      setSavingLayout(false);
    }
  };

  /**
   * Pull the source through the API client, not through the iframe.
   *
   * `<iframe src>` is a plain browser navigation: it sends cookies and nothing
   * else. This office authenticates with a token in localStorage that axios
   * attaches as a header, so a direct src on an authenticated endpoint is a
   * guaranteed 401 — which is exactly what it did. Every other image in the app
   * is a public-read S3 URL, so nothing here had ever needed a credential
   * before.
   *
   * Fetching to a blob keeps the one authenticated request on the client that
   * holds the credential, and hands the iframe something that needs none.
   */
  useEffect(() => {
    if (step !== 3 || !active?.id || published) return undefined;

    let cancelled = false;
    let created: string | undefined;
    setSourceError(undefined);
    // Cleared here rather than in the cleanup: the modal uses destroyOnClose,
    // so cleanup runs on unmount, and setting state there is the classic
    // "update on an unmounted component" warning. Clearing on entry gives the
    // same guarantee — a stale blob from a previous render is never shown.
    setSourceUrl(undefined);

    ancanaApi.get(`floor-plan-renders/${active.id}/source/`, { responseType: 'blob' })
      .then(({ data }) => {
        if (cancelled) return;
        created = URL.createObjectURL(data as Blob);
        setSourceUrl(created);
      })
      .catch(() => {
        if (!cancelled) setSourceError('The original could not be loaded from Drive.');
      });

    // Revoking matters here: these are multi-megabyte developer PDFs, and a
    // blob URL is held until it is released or the tab closes.
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [step, active?.id, published]);

  // A local preview of the file about to be uploaded. Revoked on every change,
  // for the same reason as the source blob above.
  useEffect(() => {
    if (!uploadFile) {
      setUploadUrl(undefined);
      return undefined;
    }
    const url = URL.createObjectURL(uploadFile);
    setUploadUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadFile]);

  /** Everything below the picker belongs to one level, so a switch starts it over. */
  const resetFlow = () => {
    setView('flow');
    setStep(0);
    setShownPlanId(undefined);
    setSource(undefined);
    setInstruction('');
    setRevision('');
    setAttested(false);
    setNotes('');
    setPublished(undefined);
    setUploadFile(undefined);
    renders.reset();
  };

  const switchLevel = (next: number) => {
    if (next === levelId) return;
    resetFlow();
    setLevelId(next);
  };

  const close = () => {
    setOpen(false);
    setLevels(undefined);
    setLevelId(undefined);
    setLevelsRead(false);
    resetFlow();
  };

  const generate = async () => {
    if (!source) return;
    setView('flow');
    setStep(1);
    await renders.start(source.id, instruction);
  };

  const applyKeying = (next: Partial<{
    mode: 'background' | 'all'; white: number; line: number;
  }>) => {
    const params = {
      mode: next.mode ?? mode,
      white_point: next.white ?? whitePoint,
      line_point: next.line ?? linePoint,
    };
    if (next.mode) setMode(next.mode);
    if (next.white) setWhitePoint(next.white);
    if (next.line) setLinePoint(next.line);
    // The attestation is about a specific image. Re-cutting produces a
    // different one, so a tick made before it no longer says anything — and a
    // gate that survives the thing it was inspecting is not a gate.
    setAttested(false);
    renders.reKey(params);
  };

  /**
   * Pick up a published version and carry on from its render.
   *
   * Seeding the sliders from `key_params` is not a nicety: landing on the
   * background step with the defaults showing, over an image that was cut at
   * different thresholds, means the controls are describing a picture that is
   * not the one above them.
   */
  const continueFrom = async (plan: IFloorPlan, toStep: 1 | 2) => {
    if (!plan.render) return;
    const row = await renders.adopt(plan.render);
    if (!row) return;
    setMode(row.key_params?.mode || 'background');
    if (row.key_params?.white_point != null) setWhitePoint(row.key_params.white_point);
    if (row.key_params?.line_point != null) setLinePoint(row.key_params.line_point);
    setAttested(false);
    setNotes('');
    setView('flow');
    setStep(toStep);
  };

  const startOver = () => {
    setSource(undefined);
    setInstruction('');
    setAttested(false);
    setNotes('');
    setView('flow');
    setStep(0);
  };

  // Same reasoning as the re-key reset above: moving to another attempt — from
  // the filmstrip, or by revising — replaces the image the tick referred to.
  useEffect(() => { setAttested(false); }, [active?.id]);

  const startUpload = () => {
    setUploadFile(undefined);
    setNotes('');
    setPublished(undefined);
    setView('upload');
  };

  const doUpload = async () => {
    if (!uploadFile) return;
    const plan = await renders.upload(uploadFile, notes);
    if (plan) {
      setPublished(plan);
      if (!plan.unit) {
        setLevels(prev => prev?.map(l => (l.id === plan.level ? { ...l, current_floor_plan: plan.id } : l)));
      }
      onPublished?.(plan);
    }
  };

  const doPublish = async () => {
    const plan = await renders.publish(notes);
    if (plan) {
      setPublished(plan);
      if (!plan.unit) {
        setLevels(prev => prev?.map(l => (l.id === plan.level ? { ...l, current_floor_plan: plan.id } : l)));
      }
      onPublished?.(plan);
    }
  };

  // The end of both routes: a generation published at step 4, or an upload.
  const publishedView = published && (
    <div style={{ padding: '32px 0', textAlign: 'center' }}>
      <div className="preview" style={{ maxWidth: 480, margin: '0 auto' }}>
        <img src={published.image_url || undefined} alt="Published floor plan" />
      </div>
      <h3 style={{ marginTop: 16 }}>
        {`Published as version ${published.version}`}
      </h3>
      <p className="cost">
        This is now the current floor plan
        {unitId ? ' for this unit' : ' for every unit of this design'}
        . Earlier versions are kept.
      </p>
      <Button type="primary" onClick={close}>Done</Button>
    </div>
  );

  const ready = active?.status === 'succeeded';
  const thin = (active?.transparent_fraction ?? 1) < 0.05;

  return (
    <>
      <span onClick={() => setOpen(true)}>{children}</span>

      <Modal
        visible={open}
        width={1100}
        title={`Floor plan${recordName ? ` — ${recordName}` : ''}${multiLevel && level ? ` · ${levelLabel(level)}` : ''}`}
        onCancel={close}
        maskClosable={false}
        destroyOnClose
        footer={null}
      >
        <Container>
          {multiLevel && (
            <div className="levels">
              <span className="levels-label">Level</span>
              <Radio.Group
                optionType="button"
                size="small"
                value={levelId}
                disabled={isWorking}
                onChange={e => switchLevel(e.target.value)}
                options={(levels || []).map(l => ({
                  value: l.id,
                  // Only for the design's own plans: a unit's override lives on
                  // its UnitLevel, which this list does not carry.
                  label: `${levelLabel(l)}${!unitId && !l.current_floor_plan ? ' (no plan)' : ''}`,
                }))}
              />
            </div>
          )}
          {view === 'flow' && (
            <Steps current={step} size="small">
              <Step title="Source" description="Pick the original" />
              <Step title="Generate" description="Redraw and refine" />
              <Step title="Background" description="Cut it out" />
              <Step title="Publish" description="Check, then ship" />
            </Steps>
          )}

          {error && (
            <Alert
              type="error"
              showIcon
              closable
              style={{ marginTop: 16 }}
              message="Something went wrong"
              description={error}
              onClose={renders.clearError}
            />
          )}

          {/* The first read decides which face to show, so showing either one
              before it lands would mean moving the ground under somebody who
              had already started reading. */}
          {isLoading && !active && (
            <div className="step-body"><Skeleton active paragraph={{ rows: 6 }} /></div>
          )}

          {!isLoading && view === 'saved' && shown && (
            <div className="step-body">
              <div className="saved">
                <div>
                  <div className="preview">
                    <img src={shown.image_url || undefined} alt="The saved floor plan" />
                  </div>
                  <div style={{ marginTop: 10 }}>
                    <strong>{`Version ${shown.version}`}</strong>
                    {shown.is_current
                      ? <Tag color="green" style={{ marginLeft: 8 }}>Current</Tag>
                      : <Tag style={{ marginLeft: 8 }}>Superseded</Tag>}
                    {shown.origin === 'uploaded' && (
                      <Tag style={{ marginLeft: 4 }}>Uploaded by hand</Tag>
                    )}
                  </div>
                  <ul className="facts">
                    <li>
                      {`Published ${when(shown.created_at)}`}
                      {shown.published_by_name ? ` by ${shown.published_by_name}` : ''}
                    </li>
                    {/* Drive-authored text. Rendered as a text child; the link
                        beside it is built from the file id, never from this. */}
                    {shown.source_path && <li>{shown.source_path}</li>}
                    {shown.notes && <li>{shown.notes}</li>}
                  </ul>
                  {driveFileUrl(shown.source_file_id) && (
                    <a
                      href={driveFileUrl(shown.source_file_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ExportOutlined />
                      {' '}
                      Open the original in Drive
                    </a>
                  )}
                </div>

                <div className="next">
                  {isInherited && (
                    <Alert
                      type="info"
                      showIcon
                      message="This comes from the design"
                      description={
                        'This unit has no floor plan of its own, so it shows the one '
                        + 'every unit of this design shares. Publishing from here '
                        + 'makes an override for this unit alone.'
                      }
                    />
                  )}

                  {layoutOwner && (
                    <div className="layout">
                      <div className="layout-title">Brochure layout</div>
                      <Radio.Group
                        value={layout}
                        disabled={savingLayout}
                        onChange={e => changeLayout(e.target.value)}
                      >
                        <Space direction="vertical" size={2}>
                          {LAYOUT_OPTIONS.map(o => (
                            <Radio key={o.value} value={o.value}>{o.label}</Radio>
                          ))}
                        </Space>
                      </Radio.Group>
                      <div className="layout-why">
                        {layoutOwner === 'unit'
                          ? 'For this unit\'s slide only. '
                          : unitId
                            ? 'Set on the design: it changes the slide for every unit that shares this plan. '
                            : 'For this level\'s slide in every brochure of this design. '}
                        Applies from the next generation.
                      </div>
                    </div>
                  )}

                  <Tooltip title={shown.render
                    ? ''
                    : 'This version was uploaded by hand, so there is no generation to adjust.'}
                  >
                    <Button
                      className="choice"
                      block
                      disabled={!shown.render}
                      loading={isWorking}
                      onClick={() => continueFrom(shown, 2)}
                    >
                      <div className="choice-title">Adjust the background</div>
                      <div className="choice-why">
                        Free. Re-cuts the transparency from the drawing that is
                        already there — no new image is generated.
                      </div>
                    </Button>
                  </Tooltip>

                  <Tooltip title={shown.render
                    ? ''
                    : 'This version was uploaded by hand, so there is nothing to revise.'}
                  >
                    <Button
                      className="choice"
                      block
                      disabled={!shown.render}
                      loading={isWorking}
                      onClick={() => continueFrom(shown, 1)}
                    >
                      <div className="choice-title">Change the drawing</div>
                      <div className="choice-why">
                        Costs one image call. Keeps the same original file and
                        asks for a change to what was drawn from it.
                      </div>
                    </Button>
                  </Tooltip>

                  <Button className="choice" block onClick={startOver}>
                    <div className="choice-title">Start from a different file</div>
                    <div className="choice-why">
                      Costs one image call. For when the developer sent new plans,
                      or the wrong file was used.
                    </div>
                  </Button>

                  <Button className="choice" block onClick={startUpload}>
                    <div className="choice-title">Upload a finished image</div>
                    <div className="choice-why">
                      Free. Publishes an image you already have, exactly as it is —
                      nothing is redrawn or cut out.
                    </div>
                  </Button>

                  <div className="cost">
                    {shown.is_current
                      ? `Nothing here changes what is live. Whichever route you take, `
                        + `version ${shown.version} stays current until you publish a new one.`
                      : `You are looking at version ${shown.version}, which is not the `
                        + `current one. Carrying on from it publishes a new version rather `
                        + `than restoring this one in place.`}
                  </div>

                  <Button type="primary" onClick={close}>Close</Button>
                </div>
              </div>

              {plans.length > 1 && (
                <div className="versions">
                  <div className="cost" style={{ marginBottom: 4 }}>
                    {`${plans.length} versions. Earlier ones are kept — click one to look at it.`}
                  </div>
                  {plans.map(plan => (
                    <div className="row" key={plan.id}>
                      <div className="thumb" onClick={() => setShownPlanId(plan.id)}>
                        {plan.image_url && <img src={plan.image_url} alt="" />}
                      </div>
                      <span className="who">{`Version ${plan.version}`}</span>
                      <span>
                        {when(plan.created_at)}
                        {plan.published_by_name ? ` · ${plan.published_by_name}` : ''}
                        {plan.origin === 'uploaded' ? ' · uploaded' : ''}
                      </span>
                      {plan.is_current && <Tag color="green">Current</Tag>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Conditional rather than hidden: DriveSearch runs a seeded search
              the moment it mounts, and a wizard opening on a saved plan has no
              business spending a Pathfinder query on a picker nobody asked for. */}
          {view === 'flow' && !isLoading && (
          <div className="step-body">
            {/* ---------------------------------------------------------- */}
            {step === 0 && (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Pick the developer's original"
                  description={
                    'Usually a single-page PDF in the property\'s plans folder. '
                    + 'Only PDFs and images can be redrawn. Open it in Drive first '
                    + 'if you are not sure it is the right one.'
                  }
                />
                <DriveSearch
                  compact
                  initialTerm={recordName}
                  acceptMimes={FLOOR_PLAN_SOURCE_MIMES}
                  selectedId={source?.id}
                  onSelect={setSource}
                />
                {source && (
                  <div style={{ marginTop: 12 }}>
                    {/* Drive-authored text. Rendered as a text child, never as
                        markup and never as an href. */}
                    <div className="chosen">{source.path}</div>
                    <Input.TextArea
                      style={{ marginTop: 8 }}
                      rows={2}
                      placeholder="Anything to add for this property? Optional — the house style is applied automatically."
                      value={instruction}
                      onChange={e => setInstruction(e.target.value)}
                    />
                  </div>
                )}
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Button type="link" style={{ float: 'left', paddingLeft: 0 }} onClick={startUpload}>
                    Already have a finished image? Upload it instead
                  </Button>
                  {currentPlan && (
                    <Button
                      style={{ marginRight: 8 }}
                      onClick={() => { setView('saved'); setShownPlanId(undefined); }}
                    >
                      Back to the saved plan
                    </Button>
                  )}
                  <Button type="primary" disabled={!source} onClick={generate}>
                    Generate — costs one image call
                  </Button>
                </div>
              </>
            )}

            {/* ---------------------------------------------------------- */}
            {step === 1 && (
              <>
                {!ready && (
                  <div style={{ padding: '48px 0', textAlign: 'center' }}>
                    {/* Indeterminate on purpose. The server reports a state,
                        not a percentage, and a fake percentage that stalls at
                        90% teaches people to distrust the real ones. */}
                    <Progress
                      percent={100}
                      status="active"
                      showInfo={false}
                      strokeWidth={4}
                      style={{ maxWidth: 420 }}
                    />
                    <p style={{ marginTop: 16 }}>
                      {active?.status_label || 'Starting…'}
                    </p>
                    <p className="cost">
                      This takes up to a minute. You can close this window — the
                      work carries on and the result will be here when you come back.
                    </p>
                  </div>
                )}

                {ready && (
                  <>
                    <div className="preview">
                      <img src={active?.image_url || undefined} alt="Generated floor plan" />
                    </div>

                    {chain.length > 1 && (
                      <div className="filmstrip">
                        {chain.map((row, index) => (
                          <Tooltip
                            key={row.id}
                            title={row.instruction || `Attempt ${index + 1}`}
                          >
                            <div
                              className={`frame${row.id === active?.id ? ' active' : ''}`}
                              onClick={() => renders.setActiveId(row.id)}
                            >
                              {row.image_url && <img src={row.image_url} alt="" />}
                            </div>
                          </Tooltip>
                        ))}
                      </div>
                    )}

                    <Input.Search
                      style={{ marginTop: 12 }}
                      placeholder="Not quite right? Say what to change — e.g. remove the furniture, label the terrace"
                      enterButton="Revise"
                      loading={isWorking}
                      value={revision}
                      onChange={e => setRevision(e.target.value)}
                      onSearch={value => {
                        if (!value.trim()) return;
                        renders.revise(value.trim());
                        setRevision('');
                      }}
                    />
                    <div className="cost" style={{ marginTop: 6 }}>
                      Each revision is another image call.
                      {active?.cost != null && ` This one cost $${active.cost.toFixed(3)}.`}
                      {' '}
                      Earlier attempts stay above — click one to go back to it.
                    </div>

                    <div style={{ marginTop: 16, textAlign: 'right' }}>
                      <Button onClick={() => setStep(0)} style={{ marginRight: 8 }}>Back</Button>
                      <Button type="primary" onClick={() => setStep(2)}>Next</Button>
                    </div>
                  </>
                )}
              </>
            )}

            {/* ---------------------------------------------------------- */}
            {step === 2 && (
              <>
                <div className="preview">
                  <img
                    src={active?.keyed_image_url || active?.image_url || undefined}
                    alt="Floor plan with the background cut out"
                  />
                </div>

                {thin && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 12 }}
                    message="Almost nothing was cut out"
                    description={
                      'The drawing does not sit on a white background, so it cannot be '
                      + 'made transparent. Go back and generate again, or publish it as '
                      + 'it is and accept an opaque image.'
                    }
                  />
                )}

                <div style={{ marginTop: 16 }}>
                  <Radio.Group
                    value={mode}
                    onChange={e => applyKeying({ mode: e.target.value })}
                  >
                    <Radio.Button value="background">Background only</Radio.Button>
                    <Radio.Button value="all">Every white area</Radio.Button>
                  </Radio.Group>
                  <div className="cost" style={{ marginTop: 6 }}>
                    <strong>Background only</strong> keeps the rooms solid and clears
                    just the area around the plan — almost always what you want.
                    <strong> Every white area</strong> clears the room interiors too,
                    which reads as a wireframe over a coloured page.
                  </div>
                </div>

                <div style={{ marginTop: 16, maxWidth: 520 }}>
                  <div>White level — anything lighter than this is background</div>
                  <Slider
                    min={linePoint + 1}
                    max={255}
                    value={whitePoint}
                    onChange={setWhitePoint}
                    onAfterChange={value => applyKeying({ white: value as number })}
                  />
                  <div>Line level — anything darker than this is line work</div>
                  <Slider
                    min={0}
                    max={whitePoint - 1}
                    value={linePoint}
                    onChange={setLinePoint}
                    onAfterChange={value => applyKeying({ line: value as number })}
                  />
                  <div className="cost">
                    Re-cutting is free and instant — it never generates a new image.
                    {active?.transparent_fraction != null
                      && ` ${Math.round(active.transparent_fraction * 100)}% of the image is now transparent.`}
                  </div>
                </div>

                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Button onClick={() => setStep(1)} style={{ marginRight: 8 }}>Back</Button>
                  <Button type="primary" loading={isWorking} onClick={() => setStep(3)}>
                    Next
                  </Button>
                </div>
              </>
            )}

            {/* ---------------------------------------------------------- */}
            {step === 3 && !published && (
              <>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Check the rooms against the original"
                  description={
                    'The model redraws from the original and can rename or drop a room '
                    + 'while keeping the building the right shape — on the first real '
                    + 'test it labelled a wing of bedrooms as a garage. Count the '
                    + 'bedrooms and read the labels before you publish.'
                  }
                />

                <div className="side-by-side">
                  <div>
                    <div className="pane-title">The developer&apos;s original</div>
                    {/* Sandboxed: this is a Drive-authored file and a PDF is a
                        scripting host. The server sends `Content-Security-Policy:
                        sandbox` too — this is the second lock, not the only one. */}
                    {sourceError ? (
                      <Alert type="warning" showIcon message="Could not load the original"
                        description={sourceError}
                      />
                    ) : (
                      <iframe
                        title="Source floor plan"
                        sandbox=""
                        src={sourceUrl}
                      />
                    )}
                    {(source?.drive_url || driveFileUrl(active?.source_file_id)) && (
                      // Always offered, not just on failure. Browsers are
                      // inconsistent about rendering a PDF inside a sandboxed
                      // frame, and an attestation nobody can actually check is
                      // the one thing this step must not become.
                      //
                      // The fallback carries the case that did not exist before:
                      // arriving here from a saved version, where no node was
                      // picked in this session and the only provenance is what
                      // the render itself recorded.
                      <a
                        href={source?.drive_url || driveFileUrl(active?.source_file_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'inline-block', marginTop: 8 }}
                      >
                        Open the original in Drive
                      </a>
                    )}
                  </div>
                  <div>
                    <div className="pane-title">What will be published</div>
                    <div className="preview">
                      <img
                        src={active?.keyed_image_url || active?.image_url || undefined}
                        alt="Floor plan to publish"
                      />
                    </div>
                  </div>
                </div>

                <Input.TextArea
                  style={{ marginTop: 12 }}
                  rows={2}
                  placeholder="Notes for whoever looks at this version later. Optional."
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />

                <Checkbox
                  style={{ marginTop: 12 }}
                  checked={attested}
                  onChange={e => setAttested(e.target.checked)}
                >
                  I have compared this against the original. The layout and the room
                  names match.
                </Checkbox>

                <div style={{ marginTop: 16, textAlign: 'right' }}>
                  <Button onClick={() => setStep(2)} style={{ marginRight: 8 }}>Back</Button>
                  <Button
                    type="primary"
                    disabled={!attested}
                    loading={isWorking}
                    onClick={doPublish}
                  >
                    Publish as the current floor plan
                  </Button>
                </div>
              </>
            )}

            {step === 3 && publishedView}
          </div>
          )}

          {view === 'upload' && !isLoading && (
            <div className="step-body">
              {published ? publishedView : (
                <>
                  <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="Upload a finished floor plan"
                    description={
                      'It is published exactly as it is: nothing is redrawn and the '
                      + 'background is not cut out. Use a PNG with a transparent '
                      + 'background if the brochure should show the page through it.'
                    }
                  />
                  {uploadUrl ? (
                    <div className="preview">
                      <img src={uploadUrl} alt="Floor plan to upload" />
                    </div>
                  ) : (
                    <Upload.Dragger
                      accept={FLOOR_PLAN_UPLOAD_MIMES.join(',')}
                      showUploadList={false}
                      // Nothing goes up on pick: the file waits for the publish
                      // button, so it can be looked at first.
                      beforeUpload={file => {
                        if (FLOOR_PLAN_UPLOAD_MIMES.includes(file.type)) setUploadFile(file);
                        else {
                          notification.error({
                            message: 'Not an image that can be published',
                            description: 'Upload a PNG, JPEG or WebP.',
                            placement: 'bottomRight',
                          });
                        }
                        return false;
                      }}
                    >
                      <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                      <p className="ant-upload-text">Click or drop an image here</p>
                      <p className="ant-upload-hint">PNG, JPEG or WebP</p>
                    </Upload.Dragger>
                  )}
                  {uploadFile && (
                    <div className="chosen" style={{ marginTop: 8 }}>
                      {uploadFile.name}
                      {' · '}
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setUploadFile(undefined)}>
                        Choose another
                      </Button>
                    </div>
                  )}

                  <Input.TextArea
                    style={{ marginTop: 12 }}
                    rows={2}
                    placeholder="Notes for whoever looks at this version later. Optional."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                  />

                  <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Button
                      style={{ marginRight: 8 }}
                      disabled={isWorking}
                      onClick={() => {
                        setUploadFile(undefined);
                        setNotes('');
                        setShownPlanId(undefined);
                        setView(currentPlan ? 'saved' : 'flow');
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      type="primary"
                      disabled={!uploadFile}
                      loading={isWorking}
                      onClick={doUpload}
                    >
                      Publish as the current floor plan
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </Container>
      </Modal>
    </>
  );
};

export default FloorPlanWizard;
